import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar, Search, MapPin, Clock, User, Phone,
  CheckCircle, XCircle, AlertCircle, Building2,
  Briefcase, MessageCircle, CalendarPlus, Filter,
  Loader2, Star, ChevronRight, Mail
} from 'lucide-react';
import AdBanner from '../../components/AdBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MedicalRepDashboard = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('search');
  
  // Data states
  const [professionals, setProfessionals] = useState([]);
  const [myAppointments, setMyAppointments] = useState([]);
  const [searchFilters, setSearchFilters] = useState({
    specialty: 'all',
    city: '',
    accepts_reps: 'all'
  });
  
  // Modal states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prosRes, aptsRes] = await Promise.all([
        axios.get(`${API_URL}/api/professionals`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/medical-rep/appointments`, { headers }).catch(() => ({ data: { appointments: [] } }))
      ]);
      
      setProfessionals(prosRes.data || []);
      setMyAppointments(aptsRes.data.appointments || []);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter professionals
  const filteredProfessionals = professionals.filter(pro => {
    if (searchFilters.specialty && searchFilters.specialty !== 'all' && pro.profile?.specialty !== searchFilters.specialty) return false;
    if (searchFilters.city && !pro.profile?.city?.toLowerCase().includes(searchFilters.city.toLowerCase())) return false;
    if (searchFilters.accepts_reps === 'yes' && !pro.profile?.accepts_medical_reps) return false;
    if (searchFilters.accepts_reps === 'no' && pro.profile?.accepts_medical_reps) return false;
    return true;
  });

  // Request appointment
  const requestAppointment = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/medical-rep/request-appointment`, {
        professional_id: selectedProfessional.id,
        date: selectedDate,
        time: selectedTime,
        message: requestMessage
      }, { headers });
      
      toast.success('Demande de visite envoyée !');
      setShowRequestModal(false);
      setRequestMessage('');
      setSelectedDate('');
      setSelectedTime('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la demande');
    }
  };

  // Cancel appointment
  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette visite ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/medical-rep/appointments/${appointmentId}`, { headers });
      toast.success('Visite annulée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const specialties = [
    // Médecins Généralistes & Spécialistes
    'Médecin Généraliste', 'Interniste', 'Cardiologue', 'Dermatologue', 
    'Gastro-entérologue', 'Pneumologue', 'Neurologue', 'Psychiatre', 'Psychologue',
    'Endocrinologue', 'Rhumatologue', 'Néphrologue', 'Urologue', 'Gynécologue',
    'Obstétricien', 'Pédiatre', 'Gériatre', 'Oncologue', 'Hématologue',
    'Allergologue', 'Immunologue', 'Infectiologue', 'Ophtalmologue', 'ORL',
    // Chirurgiens
    'Chirurgien Général', 'Chirurgien Orthopédiste', 'Chirurgien Cardiaque',
    'Neurochirurgien', 'Chirurgien Plasticien / Esthétique', 'Chirurgien Vasculaire',
    'Anesthésiste-Réanimateur',
    // Dentaire
    'Dentiste', 'Orthodontiste', 'Chirurgien-Dentiste',
    // Paramédical
    'Kinésithérapeute', 'Ostéopathe', 'Chiropracteur', 'Orthophoniste',
    'Ergothérapeute', 'Psychomotricien', 'Infirmier(ère)', 'Sage-Femme',
    'Diététicien(ne)', 'Podologue', 'Pédicure', 'Pédicure-Podologue',
    // Bien-être
    'Réflexologue', 'Acupuncteur', 'Naturopathe', 'Homéopathe', 
    'Sophrologue', 'Hypnothérapeute',
    // Esthétique
    'Esthéticien(ne)', 'Médecin Esthétique',
    // Autres
    'Opticien', 'Audioprothésiste', 'Pharmacien', 'Radiologue'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Espace Représentant de Laboratoires</h1>
              <p className="text-slate-600">Bienvenue, {user?.first_name} {user?.last_name}</p>
            </div>
          </div>
          {user?.profile?.company_name && (
            <Badge className="mt-2 bg-amber-100 text-amber-800">
              <Building2 className="w-3 h-3 mr-1" />
              {user.profile.company_name}
            </Badge>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{myAppointments.filter(a => a.status === 'confirmed').length}</p>
              <p className="text-sm text-slate-600">Visites confirmées</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{myAppointments.filter(a => a.status === 'pending').length}</p>
              <p className="text-sm text-slate-600">En attente</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <User className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{professionals.filter(p => p.profile?.accepts_medical_reps).length}</p>
              <p className="text-sm text-slate-600">Pros acceptent visites</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <MapPin className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{user?.profile?.territory || 'Luxembourg'}</p>
              <p className="text-sm text-slate-600">Territoire</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="search" className="gap-2">
              <Search className="w-4 h-4" />
              Rechercher
            </TabsTrigger>
            <TabsTrigger value="appointments" className="gap-2">
              <Calendar className="w-4 h-4" />
              Mes Visites
            </TabsTrigger>
          </TabsList>

          {/* Search Tab */}
          <TabsContent value="search">
            {/* Filters */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtres de recherche
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Spécialité</Label>
                    <Select 
                      value={searchFilters.specialty}
                      onValueChange={(v) => setSearchFilters({...searchFilters, specialty: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Toutes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les spécialités</SelectItem>
                        {specialties.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input 
                      placeholder="Ex: Luxembourg"
                      value={searchFilters.city}
                      onChange={(e) => setSearchFilters({...searchFilters, city: e.target.value})}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Accepte les visites</Label>
                    <Select 
                      value={searchFilters.accepts_reps}
                      onValueChange={(v) => setSearchFilters({...searchFilters, accepts_reps: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="yes">Oui - Accepte</SelectItem>
                        <SelectItem value="no">Non précisé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-end">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => setSearchFilters({ specialty: 'all', city: '', accepts_reps: 'all' })}
                    >
                      Réinitialiser
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-4">
              <p className="text-slate-600">{filteredProfessionals.length} professionnels trouvés</p>
              
              {filteredProfessionals.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Aucun professionnel trouvé</p>
                  </CardContent>
                </Card>
              ) : (
                filteredProfessionals.map((pro) => (
                  <Card key={pro.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div className="flex gap-4">
                          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
                            {pro.first_name?.[0]}{pro.last_name?.[0]}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">
                              Dr. {pro.first_name} {pro.last_name}
                            </h3>
                            <p className="text-slate-600">{pro.profile?.specialty}</p>
                            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                              <MapPin className="w-4 h-4" />
                              {pro.profile?.city}, {pro.profile?.address}
                            </div>
                            {pro.profile?.accepts_medical_reps ? (
                              <Badge className="mt-2 bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Accepte les visites de représentants
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="mt-2 text-slate-500">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Contacter pour confirmer
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button 
                            className="gap-2"
                            onClick={() => {
                              setSelectedProfessional(pro);
                              setShowRequestModal(true);
                            }}
                          >
                            <CalendarPlus className="w-4 h-4" />
                            Demander une visite
                          </Button>
                          {pro.phone && (
                            <Button variant="outline" className="gap-2" asChild>
                              <a href={`tel:${pro.phone}`}>
                                <Phone className="w-4 h-4" />
                                Appeler
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <div className="space-y-4">
              {myAppointments.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Aucune visite programmée</p>
                    <Button className="mt-4" onClick={() => setActiveTab('search')}>
                      Rechercher des professionnels
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                myAppointments.map((apt) => (
                  <Card key={apt.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge 
                              className={
                                apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                apt.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }
                            >
                              {apt.status === 'confirmed' ? 'Confirmé' :
                               apt.status === 'pending' ? 'En attente' : 'Annulé'}
                            </Badge>
                            <span className="text-slate-500 text-sm">
                              {new Date(apt.date).toLocaleDateString('fr-FR')} à {apt.time}
                            </span>
                          </div>
                          <h3 className="font-semibold">
                            Dr. {apt.professional_name}
                          </h3>
                          <p className="text-sm text-slate-600">{apt.professional_specialty}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {apt.location}
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          {apt.status !== 'cancelled' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => cancelAppointment(apt.id)}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Annuler
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Request Appointment Modal */}
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Demander une visite</DialogTitle>
              <DialogDescription>
                {selectedProfessional && (
                  <span>
                    Dr. {selectedProfessional.first_name} {selectedProfessional.last_name} - {selectedProfessional.profile?.specialty}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date souhaitée</Label>
                  <Input 
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure souhaitée</Label>
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="09:00">09:00</SelectItem>
                      <SelectItem value="10:00">10:00</SelectItem>
                      <SelectItem value="11:00">11:00</SelectItem>
                      <SelectItem value="14:00">14:00</SelectItem>
                      <SelectItem value="15:00">15:00</SelectItem>
                      <SelectItem value="16:00">16:00</SelectItem>
                      <SelectItem value="17:00">17:00</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Message (optionnel)</Label>
                <Textarea 
                  placeholder="Présentez-vous et expliquez l'objet de votre visite..."
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  rows={4}
                />
              </div>
              
              {!selectedProfessional?.profile?.accepts_medical_reps && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Ce professionnel n'a pas précisé s'il accepte les visites. Il pourra refuser votre demande.
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestModal(false)}>
                Annuler
              </Button>
              <Button onClick={requestAppointment}>
                <Mail className="w-4 h-4 mr-2" />
                Envoyer la demande
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Ad Banner for Lab Reps */}
      <AdBanner position="bottom" />
    </div>
  );
};

export default MedicalRepDashboard;
